<script setup>
import { defineProps, ref, computed } from 'vue';
import { Modal, Popover, AppButton } from '@n11/so-ui-components';
import { OFFER_STATUS } from '@/utils/enum/offerStatus';
import CurrencyInput from '@/components/DataTable/CurrencyInput';
import { modifyDeal, applyDeal } from '../services/productDealService';
import { useStore } from 'vuex' 

const acceptOfferModalRef = ref(null);
const acceptNewOfferModalRef = ref(null);
const popoverRef = ref(null);
const newSalesPrice = ref();
const errorText = ref();
const isLoading = ref(false);
const store = useStore();

const props = defineProps({
  // Same type with OfferStatus. Because it is determined according to offers.
  dealStatus: {
    type: String,
    required: true,
    default: OFFER_STATUS.WAITING_FOR_ACCEPTANCE,
  },
  startDate: {
    type: Date,
    required: true,
  },
  commission: {
    type: Number,
    required: true,
  },
  price: {
    type: Number,
    required: true,
  },
  upperPrice: {
    type: Number,
    required: true,
  },
  lowerPrice: {
    type: Number,
    required: true,
  },
  dealId: {
    type: Number,
    required: true,
  },
  groupId: {
    type: Number,
    required: true,
  },
  categoryId: {
    type: Number,
    required: true,
  },
  productId: {
    type: Number,
    required: true,
  },
  commissionRates: {
    type: Object,
    required: true,
  },
  hasVariants: {
    type: Boolean,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
});

const currencyInputOptions = { 
  locale: 'tr-TR',
  currency: 'TRY',
  currencyDisplay: 'symbol',
  hideCurrencySymbolOnFocus: false,
  hideGroupingSeparatorOnFocus: false,
  hideNegligibleDecimalDigitsOnFocus: false,
  autoDecimalDigits: false,
  useGrouping: true,
  accountingSign: false,
  valueRange: { min: 0 },
};

const isPlanned = computed(() => props.startDate.getTime() > new Date().getTime());

const formatPrice = (price) => {
  return price.toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'TRY',
  });
};

const getbuttonText = () => {
  if (
    props.dealStatus === OFFER_STATUS.WAITING_FOR_ACCEPTANCE ||
    props.dealStatus === OFFER_STATUS.REFUSED
  ) {
    return 'Fiyat Teklifini Gir';
  }

  return 'Yeni Fiyat Teklifini Gir';
};

const addNewOffer = () => {
  if (props.dealStatus === OFFER_STATUS.WAITING_FOR_ACCEPTANCE) {
    acceptOfferModalRef.value.openModal();
  } else if (props.dealStatus === OFFER_STATUS.ACCEPTED || props.dealStatus === OFFER_STATUS.SUPERSEDED || props.dealStatus === OFFER_STATUS.WAITING_FOR_APPLYING ) {
    acceptNewOfferModalRef.value.openModal();
  }
};

const handleContinue = () => {
  if (newSalesPrice.value > props.upperPrice) {
    errorText.value = `Avantajlı komisyondan faydalanmanız için bu ürünün fiyatı en fazla ₺${props.upperPrice} olmalıdır.`;
  } else if (newSalesPrice.value < props.lowerPrice) {
    errorText.value = `Bu kategorideki fiyat en az ₺${props.lowerPrice} olmalıdır.`;
  } else {
    if (isPlanned.value || newSalesPrice.value <= props.price) {
      errorText.value = '';
      addNewOffer();
    } else {
      errorText.value = `Avantajlı komisyondan faydalanmanız için bu ürünün fiyatı en fazla ₺${props.price} olmalıdır.`;
    }
  }
}

const handleModifyDeal = async () => {
  isLoading.value = true;

  try {
    await modifyDeal({
      dealId: props.dealId,
      groupId: props.groupId,
      price: newSalesPrice.value,
      categoryId: props.categoryId,
      productId: props.productId,
    });

    window.location.reload();
  } catch (error) {
    console.error(error);
    acceptNewOfferModalRef.value.hiddenModal();
    popoverRef.value.popoverToogle();
  }
  isLoading.value = false;
}

const handleApplyDeal = async () => {
  isLoading.value = true;

  try {
    await applyDeal({
      dealId: props.dealId,
      groupId: props.groupId,
      price: newSalesPrice.value,
      categoryId: props.categoryId,
      productId: props.productId,
    })

    window.location.reload();
  } catch (error) {
    isLoading.value = false;
    acceptOfferModalRef.value.hiddenModal();
    popoverRef.value.popoverToogle();
    store.commit("common/SET_TOASTER_DATA", {
      type: 'info',
      title: '',
      description: error.data.message,
    });
  }
  isLoading.value = false;
}

const newCommissionRate = () => {
  const offers = ['advantageousOffer', 'superOffer', 'shockingOffer'];

  for (const offer of offers) {
    const { upperPrice, lowerPrice, commission } = props.commissionRates[offer];
    if (upperPrice >= newSalesPrice.value && newSalesPrice.value >= lowerPrice) {
      return commission;
    }
  }
  
  return props.commission;
};
</script>

<template>
  <div class="seller-price-offer">
    <p class="text">Mevcut satış fiyatı ve komisyon:</p>
    <p class="price">{{ formatPrice(props.price) }}</p>
    <p class="commission">%{{ props.commission }} Komisyon</p>
    <div class="popover-wrapper">
      <Popover
        ref="popoverRef"
        position="top-center"
        color="unset"
        :popoverStyle="{ padding: '0px', left: '88px', top: '56px' }"
        :withOverlay="true"
      >
        <template v-slot:content="{ on }">
          <AppButton
            v-on="on"
            :disabled="dealStatus === OFFER_STATUS.REFUSED"
            :title="getbuttonText()"
            primary
            xsm
            class="popover-wrapper__btn"
          />
        </template>

        <template #popoverContent>
          <div class="popover-wrapper__content">
            <p class="popover-wrapper__content__title">Yeni satış fiyatını gir</p>
            <div v-if="hasVariants">
              <p class="all-variants-badge">Tüm Varyantlarda Geçerli</p>
              <p class="all-variants-description">Gireceğiniz fiyat, ürünün <span class="all-variants__bold">tüm varyantlarına</span> uygulanacak.</p>
            </div>
            <div v-else>
              <p class="all-variants-badge">Tek Varyantta Geçerli</p>
              <p class="all-variants-description">Gireceğiniz fiyat, ürünün <span class="all-variants__bold">tek varyantına</span> uygulanacak.</p>
            </div>
            <CurrencyInput
              ref="currencyInput"
              v-model="newSalesPrice"
              class="popover-wrapper__content__input"
              :options="currencyInputOptions"
              :class="[{ error: errorText }]"
            />
            <p v-if="errorText" class="popover-wrapper__content__error-text">{{ errorText }}</p>
            <AppButton
              title="Devam Et"
              md
              primary
              @click="handleContinue"
            />
          </div>
        </template>
      </Popover>
    </div>

    <Modal
      ref="acceptOfferModalRef"
      type="success"
      title="Teklifi onaylıyor musunuz?"
      negativeActionText="Geri Dön"
      positiveActionText="Onayla"
      :isPositiveActionButtonDisabled="isLoading"
      :isPositiveActionPending="isLoading"
      @onPositiveActionClick="handleApplyDeal"
      @onNegativeActionClick="acceptOfferModalRef.hiddenModal()"
    >
      <template v-slot:body>
        <div class="accept-body">
          <p v-if="hasVariants">
            Onaylarsanız seçtiğiniz tarih aralığında aşağıdaki yeni fiyat ve komisyon oranı, ürünün 
            <span class="all-variants__bold">tüm varyantlarında</span> 
            geçerli olacaktır.
          </p>
          <p v-else>
            Onaylarsanız seçtiğiniz tarih aralığında aşağıdaki yeni fiyat ve komisyon oranı geçerli olacaktır.
          </p>
          <div class="change-offer">
            <div class="change-offer__old">
              <span class="title">Mevcut Fiyat & Komisyon</span>
              <span class="price">{{ formatPrice(price) }}</span>
              <span class="commission">%{{ commission }} Komisyon</span>
            </div>
            <div class="change-offer-new">
              <span class="title">Yeni Teklif</span>
              <span class="price">{{ formatPrice(newSalesPrice) }}</span>
              <span class="commission">%{{ newCommissionRate() }} Komisyon</span>
            </div>
          </div>
          <div class="description">
            Bu teklif, yalnızca belirtilen tarihler arasında ve tek sefer için
            geçerlidir. Tekliften bir kez vazgeçildiğinde tekrar yararlanılamaz,
            teklif koşullarındaki fiyat ve komisyon oranı geçerliliğini yitirir.
          </div>
        </div>
      </template>
    </Modal>

    <Modal
      ref="acceptNewOfferModalRef"
      type="info"
      title="Bu teklife geçiş yapmayı onaylıyor musunuz?"
      negativeActionText="Geri Dön"
      positiveActionText="Onayla"
      :isPositiveActionButtonDisabled="isLoading"
      :isPositiveActionPending="isLoading"
      @onPositiveActionClick="handleModifyDeal"
      @onNegativeActionClick="acceptNewOfferModalRef.hiddenModal()"
    >
      <template v-slot:body>
        <div class="accept-body">
          <p v-if="hasVariants">Onaylarsanız seçtiğiniz tarih aralığında aşağıdaki yeni fiyat ve komisyon oranı, ürünün <span class="all-variants__bold">tüm varyantlarında</span> geçerli olacaktır.</p>
          <p v-else>
            Onaylarsanız belirtilen tarihlerde geçiş yapacağınız teklif geçerli
            olacak.
          </p>
          <div class="change-offer">
            <div class="change-offer__old">
              <span class="title">Mevcut Fiyat & Komisyon</span>
              <span class="price">{{ formatPrice(price) }}</span>
              <span class="commission">%{{ commission }} Komisyon</span>
            </div>
            <div class="change-offer-new">
              <span class="title">Yeni Teklif</span>
              <span class="price">{{ formatPrice(newSalesPrice) }}</span>
              <span class="commission">%{{ newCommissionRate() }} Komisyon</span>
            </div>
          </div>
          <div class="description">
            Bu teklif, yalnızca belirtilen tarihler arasında ve tek sefer için
            geçerlidir. Tekliften bir kez vazgeçildiğinde tekrar yararlanılamaz,
            teklif koşullarındaki fiyat ve komisyon oranı geçerliliğini yitirir.
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/variables';
@import '@/assets/styles/utilities';

.seller-price-offer {
  height: 100%;
  padding: 16px 12px 16px 12px;

  p {
    &.text {
      color: $colorD50;
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
      margin-bottom: 4px;
    }

    &.price {
      color: $colorM50;
      font-size: 13px;
      font-weight: 700;
      line-height: 18px;
      margin-bottom: 4px;
    }

    &.commission {
      border: 1px solid $colorM20;
      border-radius: 4px;
      color: $colorM50;
      font-size: 11px;
      font-weight: 600;
      line-height: 14px;
      padding: 2px 6px;
      width: fit-content;
    }
  }

  .popover-wrapper {
    .popover {
      width: 100%;
    }

    &__btn {
      margin-top: 12px;
      font-size: 11px !important;
      height: 24px !important;
    }

    &__content {
      border-radius: 8px;
      border: 1px solid var(--Dusk---Decorative-D40, $colorD40);
      box-shadow: 0 0 0 3px $colorD20;
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 197px;
      padding: 12px;
      background-color: $colorN00;

      &__title {
        color: $colorN80;
        font-size: 12px;
        font-style: normal;
        font-weight: 700;
        line-height: 16px;
        text-align: start;
      }

      &__error-text {
        color: $colorE50;
        font-size: 11px;
        font-weight: 400;
        line-height: 14px;
        text-align: left;
      }

      &__input {
        &.error {
          border: 1px solid $colorE50;
          outline: 3px solid $colorE10;
        }
      }
    }

    .all-variants {
      &-badge {
      color: #3C77BC;
      font-size: 10px;
      line-height: 12px;
      padding: 3px 6px;
      border: 1px solid #8AADD7;
      border-radius: 4px;
      background-color: #EFF4FA;
      white-space: nowrap;
      text-align: start;
      width: fit-content;
    }

    &-description {
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
      color: #4E5C78;
      text-align: left;
      margin-top: 4px;
      }
    }
  }

  .all-variants__bold {
    font-weight: 800;
  }

  .accept-body {
    gap: 16px;
    display: flex;
    flex-direction: column;

    p {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: -0.05px;
      color: $colorN80;
    }

    .change-offer {
      display: flex;
      padding-top: 10px;
      align-items: center;
      gap: 20px;
      align-self: stretch;

      &__old {
        background-color: $colorD05;
        &::after {
          content: '';
          width: 24px;
          height: 24px;
          background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgaWQ9ImFycm93X3dhcm1fdXBfMjRkcF81RjYzNjhfRklMTDBfd2dodDQwMF9HUkFEMF9vcHN6MjQgNiI+CjxnIGlkPSJWZWN0b3IiPgo8cGF0aCBkPSJNNS42IDhIOS4yVjE2SDUuNlY4Wk0yIDhINC40VjE2SDJWOFoiIGZpbGw9IiNEM0Q2REYiLz4KPHBhdGggZD0iTTEwLjQgOEgxN1YxNkgxMC40VjhaIiBmaWxsPSIjRDNENkRGIi8+CjwvZz4KPHBhdGggaWQ9IlBvbHlnb24gMSIgZD0iTTIyIDEyTDEzLjc1IDE5Ljc5NDJMMTMuNzUgNC4yMDU3N0wyMiAxMloiIGZpbGw9IiNEM0Q2REYiLz4KPC9nPgo8L3N2Zz4K");
          position: absolute;
          right: -22px;
          z-index: 9;
        }
      }

      > div {
        position: relative;
        display: flex;
        padding: 24px 24px 16px 24px;
        flex-direction: column;
        flex: 1 0 0;
        align-self: stretch;
        border-radius: 6px;
        border: 1px solid $colorD10;
        align-items: center;

        .title {
          width: max-content;
          display: flex;
          gap: 2px;
          padding: 4px 8px;
          justify-content: center;
          align-items: center;
          position: absolute;
          left: 50%;
          -webkit-transform: translateX(-50%);
          transform: translateX(-50%);
          top: -9px;
          border-radius: 6px;
          border: 0.5px solid $colorD20;
          background: $colorN00;
          color: $colorD50;
          font-size: 10px;
          font-weight: 600;
          line-height: 12px;
        }

        .price {
          color: var(--Neutral---Background-N80, #242424);
          font-size: 16px;
          font-weight: 700;
          line-height: 20px;
          letter-spacing: -0.05px;
        }

        .commission {
          display: flex;
          height: 18px;
          padding: 2px 4px;
          align-items: center;
          gap: 4px;
          border-radius: 4px;
          border: 1px solid $colorD20;
          background: $colorN00;
          color: $colorD50;
          text-align: center;
          font-size: 10px;
          font-weight: 600;
          line-height: 12px;
          margin-top: 4px;
        }

        &.change-offer-new {
          border: 1px solid $colorP20;
          background: $colorM05;

          .title {
            color: white;
            background: $colorM50;
            line-height: 10px;
          }

          .price {
            color: $colorM50;
          }

          .commission {
            border: 1px solid $colorP20;
            color: $colorM50;
          }
        }
      }
    }

    .description {
      gap: 8px;
      display: flex;
      padding: 16px;
      align-items: flex-start;
      border-radius: 6px;
      font-size: 13px;
      line-height: 18px;
      letter-spacing: -0.05px;
      text-align: start;
      background: $colorB05;
      color: $colorN80;

      &::before {
        content: "";
        width: 24px;
        min-width: 24px;
        height: 24px;
        display: block;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:v='https://vecta.io/nano'%3E%3Ccircle cx='12' cy='12' r='10' fill='%234fa4e5'/%3E%3Cpath d='M12 11a1 1 0 0 1 .993.884L13 12v4a1 1 0 0 1-1.993.116L11 16v-4a1 1 0 0 1 1-1zm0-4a1 1 0 1 1 0 2 1 1 0 1 1 0-2z' fill='%23fff'/%3E%3C/svg%3E");
      }
    }
  }
}
</style>
